/*
 * Copyright (c) 2017, Indigen Solutions. All rights reserved.
 *
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 *
 * This software is the confidential and proprietary information.
 *
 * You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement
 * you entered into.
 */

import * as moment from "moment";
import { filter, map, chain, isEmpty, get } from "lodash";
import { User } from "../models/User.model";
import { Customer } from "../types/Customer";
import { Quotation } from "../types/Quotation";
import { ExcelHelper } from "./Excel.helper";
import { PDFHelper } from "./PDF.helper";
import { ProductGO } from "../types/ProductGO";
import { ProductSPR } from "../types/ProductSPR";
import { ProductGPI } from "../types/ProductGPI";
import { Beneficiary } from "../types/Beneficiary";
import { Item } from "../types/Item";
import { Document, Base64Document } from "../types/Document";
import { services } from "../services";
import { concat, compact } from "lodash";
import { PDFFormHelper } from "./PDFForm.helper";

/**
 *
 */
export abstract class ProductHelper {
    protected variant: string;

    protected advisor: User = null;
    protected customer: Customer = null;
    protected quotation: Quotation = null;
    protected product: any = null;

    protected tarifsXLS: any = null;
    protected propositionPDF: PDFFormHelper = null;
    protected adhesionPDF: PDFFormHelper = null;
    protected courrierAdhesionPDF: PDFFormHelper = null;
    protected courrierPropositionPDF: PDFFormHelper = null;

    // Contructor

    public constructor(variant: string = "") {
        this.variant = variant;
        this.tarifsXLS = new ExcelHelper();
        this.propositionPDF = new PDFFormHelper();
        this.adhesionPDF = new PDFFormHelper();
        this.courrierAdhesionPDF = new PDFFormHelper("Courrier d'accompagnement");
        this.courrierPropositionPDF = new PDFFormHelper("Courrier d'accompagnement");
    }

    public init(advisor: User, customer: Customer, quotation: Quotation) {
        window.console.log("init :", advisor);
        this.advisor = advisor;
        this.customer = customer;
        this.quotation = quotation;
        this.product = quotation.product;
    }

    // Setter

    public setQuotation(quotation: Quotation) {
        this.quotation = quotation;
        this.product = quotation.product;
    }

    public setCustomer(customer: Customer) {
        this.customer = customer;
    }

    // Proposition management

    public showPropositionPDF(documents: Document[]) {
        this.propositionPDF.show(this.getPDFields());
    }

    public downloadPropositionPDF(documents: Document[]) {
        this.propositionPDF.download(this.getPDFields(), `Proposition${this.variant ? this.variant : ""}.pdf`);
        this.downloadRelatedDocumentsPDF(documents);
    }

    public downloadCourrierPropositionPDF() {
        if (this.courrierPropositionPDF.isRead()) {
            this.courrierPropositionPDF.download(this.getCourrierPDFFields(), `Courrier d'accompagnement.pdf`);
        }
    }

    public async getCourrierPropositionBase64Document(): Promise<Base64Document> {
        if (this.courrierPropositionPDF.isRead()) {
            const pdfBase64 = await this.courrierPropositionPDF.getBase64(this.getCourrierPDFFields());
            return {
                base64Content: pdfBase64,
                filename: "Courrier d'accompagnement.pdf",
            };
        }
        return null;
    }

    public getPropositionFormPDFs(): PDFFormHelper[] {
        if (this.courrierPropositionPDF.isRead()) {
            return [this.courrierPropositionPDF.setFields(this.getCourrierPDFFields())];
        }
        return [];
    }

    public async printPropositionPDF() {
        this.propositionPDF.print(this.getPDFields());
    }

    public async sendPropositionPDF(
        subject: string,
        message: string,
        base64documents: Base64Document[],
        documents: Document[]
    ) {
        const pdfBase64 = await this.propositionPDF.getBase64(this.getPDFields());
        base64documents = base64documents || [];
        base64documents.push({
            base64Content: pdfBase64,
            filename: `Proposition${this.variant ? this.variant : ""}.pdf`,
        });
        const result = await this.send(subject, message, base64documents, documents);
        return result;
    }

    public getPropositionDocuments() {
        const dico = this.getDico();
        return this.filterDocuments(compact(concat(dico.docs.proposition, dico.docs.common)));
    }

    // Adhesion management

    public showAdhesionPDF(documents: Document[]) {
        this.adhesionPDF.show(this.getPDFields());
    }

    public downloadAdhesionPDF(documents: Document[]) {
        this.adhesionPDF.download(this.getPDFields(), `Adhesion${this.variant ? this.variant : ""}.pdf`);
        this.downloadRelatedDocumentsPDF(documents);
    }

    public downloadRelatedDocumentsPDF(documents: Document[]) {
        documents.forEach(async (document) => {
            if (document.isForm) {
                const pdfFormHelper = new PDFFormHelper();
                await pdfFormHelper.read(document.url);
                pdfFormHelper.download(this.getPDFields(), document.label + ".pdf");
            } else {
                new PDFHelper().download(document.url, document.label + ".pdf");
            }
        });
    }

    public downloadCourrierAdhesionPDF() {
        if (this.courrierAdhesionPDF.isRead()) {
            this.courrierAdhesionPDF.download(this.getCourrierPDFFields(), `Courrier d'accompagnement.pdf`);
        }
    }

    public async getCourrierAdhesionBase64Document(): Promise<Base64Document> {
        if (this.courrierAdhesionPDF.isRead()) {
            const pdfBase64 = await this.courrierAdhesionPDF.getBase64(this.getCourrierPDFFields());
            return {
                base64Content: pdfBase64,
                filename: "Courrier d'accompagnement.pdf",
            };
        }
        return null;
    }

    public getAdhesionFormPDFs(): PDFFormHelper[] {
        if (this.courrierAdhesionPDF.isRead()) {
            return [this.courrierAdhesionPDF.setFields(this.getCourrierPDFFields())];
        }
        return [];
    }

    public async printAdhesionPDF() {
        this.adhesionPDF.print(this.getPDFields());
    }

    public async sendAdhesionPDF(
        subject: string,
        message: string,
        base64documents: Base64Document[],
        documents: Document[]
    ) {
        const pdfBase64 = await this.adhesionPDF.getBase64(this.getPDFields());
        base64documents = base64documents || [];
        base64documents.push({
            base64Content: pdfBase64,
            filename: `Adhesion${this.variant ? this.variant : ""}.pdf`,
        });
        const result = this.send(subject, message, base64documents, documents);
        return result;
    }

    public getAdhesionDocuments() {
        const dico = this.getDico();
        return this.filterDocuments(compact(concat(dico.docs.adhesion, dico.docs.common)));
    }

    // Date

    public getAgeAtDate(birthdate: string, date: string): number {
        return -moment(birthdate).diff(moment(date), "year");
    }

    public getAgeAtEffectiveDate(): number {
        return this.getAgeAtDate(this.customer.birthdate, this.quotation.effectiveDate);
    }

    public getAge(): number {
        return -moment(this.customer.birthdate).diff(moment(), "year");
    }

    // Abstract

    protected abstract getDico();
    protected abstract getPDFields();

    // Protected

    protected getItems(rangeCells: string, filterNumbers: boolean): Item[] {
        let cells = this.tarifsXLS.getRangeCells(rangeCells);
        if (filterNumbers) {
            cells = filter(cells, (cell) => !isNaN(cell.v));
        }
        return map(cells, (cell) => {
            return {
                name: parseFloat(cell.v) + "",
                label: cell.w,
            };
        });
    }

    protected getCourrierPDFFields() {
        return {
            ...this.getAdvisorPDFFields(),
            ...this.getCustomerPDFFields(),
            effectiveDateFull: moment(this.quotation.creationDate).format("DD MMMM YYYY"),
            customerName: this.customer.lastname + " " + this.customer.firstname,
            customerTitle1: this.customer.title === "mrs" || this.customer.title === "miss" ? "Madame" : "Monsieur",
            customerTitle2: this.customer.title === "mrs" || this.customer.title === "miss" ? "Madame" : "Monsieur",
            productName: get(this.getDico(), "cover.title"),
            customerCity: this.customer.city,
            customer_lastname: this.customer.lastname + " ,",
        };
    }

    protected getAdvisorPDFFields() {
        return {
            advisor_name: this.advisor.lastname + " " + this.advisor.firstname,
            advisor_lastname: this.advisor.lastname,
            advisor_firstname: this.advisor.firstname,
            advisor_vendorCode: this.advisor.vendorCode,
            // Do not generate bringer code, cf Ticket #299
            // advisor_bringerCode: this.advisor.bringerCode,
            advisor_agencyCode: this.advisor.agencyCode,
            advisor_nsoCode: this.advisor.nsoCode,
            advisor_agentCode: this.advisor.agentCode,
            advisor_counsellorCode: this.advisor.counsellorCode,
            company_code: this.advisor.agencyCode, // TODO TO REMOVE
            company_entity: this.advisor.company.label,
            validityDate: moment(this.quotation.validityDate).format("DDMMYYYY"),
            creationDate: moment(this.quotation.creationDate).format("DDMMYYYY"),

            // ** Adhesion **

            isAdvisor: this.advisor.vendorCode || this.advisor.bringerCode || this.advisor.agencyCode,
            isValidityDate: moment(this.quotation.validityDate).format("DDMMYYYY"),
        };
    }

    protected getCustomerPDFFields(postFix = "") {
        return this.manageDuplicateFields({
            ["customer_mr" + postFix]: this.customer.title === "mr",
            ["customer_mrs" + postFix]: this.customer.title === "mrs",
            ["customer_miss" + postFix]: this.customer.title === "miss",
            ["customer_single" + postFix]: this.customer.familyStatus === "single",
            ["customer_married" + postFix]: this.customer.familyStatus === "married",
            ["customer_pacs" + postFix]: this.customer.familyStatus === "pacs",
            ["customer_widower" + postFix]: this.customer.familyStatus === "widower",
            ["customer_divorced" + postFix]: this.customer.familyStatus === "divorced",
            ["customer_freeUnion" + postFix]: this.customer.familyStatus === "freeUnion",
            ["customer_lastname" + postFix]: this.customer.lastname,
            ["customer_firstname" + postFix]: this.customer.firstname,
            ["customer_birthname" + postFix]: this.customer.birthname,
            ["customer_birthdate" + postFix]: moment(this.customer.birthdate).format("DDMMYYYY"),
            ["customer_departmentOfBirth" + postFix]: this.customer.departmentOfBirth,
            ["customer_cityOfBirth" + postFix]: this.customer.cityOfBirth,
            ["customer_countryOfBirth" + postFix]: this.customer.countryOfBirth,
            ["customer_address" + postFix]: this.customer.address,
            ["customer_zipcode" + postFix]: this.customer.zipcode,
            ["customer_city" + postFix]: this.customer.city,
            ["customer_phone" + postFix]: this.customer.phone,
            ["customer_mobile" + postFix]: this.customer.mobile,
            ["customer_email" + postFix]: this.customer.email,
            ["customer_vocation" + postFix]: this.customer.vocation,
            ["customer_optin" + postFix]: this.customer.optin,

            // ** Adhesion **

            ["customer_idDocument_passport" + postFix]: this.customer.idDocument === "passport",
            ["customer_idDocument_identityCard" + postFix]: this.customer.idDocument === "identityCard",
            ["customer_idDocument_residencePermit" + postFix]: this.customer.idDocument === "residencePermit",
            ["customer_addressProof_electricityBill" + postFix]: this.customer.addressProof === "electricityBill",
            ["customer_addressProof_phoneBill" + postFix]: this.customer.addressProof === "phoneBill",
            ["customer_addressProof_taxNotice" + postFix]: this.customer.addressProof === "taxNotice",
            ["customer_addressProof_other" + postFix]: this.customer.addressProof === "other",
            ["customer_addressProofOther" + postFix]: this.customer.addressProofOther,
        });
    }

    protected manageDuplicateFields(obj: object, count = 5) {
        let keys = Object.keys(obj);
        return keys.reduce((acc, key) => {
            if (acc[key]) {
                for (let i = 1; i < count; i++) {
                    acc[key + "_" + i] = obj[key];
                }
            }
            return acc;
        }, obj);
    }

    protected getProductBeneficiariesPDFFields() {
        const product = this.quotation.product as ProductGO | ProductSPR | ProductGPI;
        let object = {
            product_beneficiaryClause_standard: product.beneficiaryClause === "standard",
            product_beneficiaryClause_beneficiary: product.beneficiaryClause === "beneficiary",
        };
        if (product.beneficiaries && !isEmpty(product.beneficiaries[0])) {
            return chain(product.beneficiaries)
                .reduce((result, beneficiary: Beneficiary, index: number) => {
                    result[`product_beneficiary_${index + 1}_lastname`] = beneficiary.lastname;
                    result[`product_beneficiary_${index + 1}_birthname`] = beneficiary.birthname;
                    result[`product_beneficiary_${index + 1}_firstname`] = beneficiary.firstname;
                    result[`product_beneficiary_${index + 1}_birthdate`] = moment(beneficiary.birthdate).format(
                        "DDMMYYYY"
                    );
                    result[`product_beneficiary_${index + 1}_cityOfBirth`] = beneficiary.cityOfBirth;
                    result[`product_beneficiary_${index + 1}_address`] = beneficiary.address;
                    result[`product_beneficiary_${index + 1}_quotePart`] = beneficiary.quotePart + "%";
                    return result;
                }, object)
                .value();
        }
        return object;
    }

    protected round(value: number): number {
        return Math.round(value * 100) / 100;
    }

    protected async send(subject: string, message: string, base64documents: Base64Document[], documents: Document[]) {
        const helper = new PDFHelper();

        let files = await Promise.all(
            documents.map(async (document) => {
                let base64Content = await helper.getBase64Content(document.url);
                return {
                    base64Content: base64Content,
                    filename: document.label + ".pdf",
                };
            })
        );

        if (!files) files = [];

        return services.api.mail.send(
            {
                email: this.advisor.email,
                name: `${this.advisor.firstname} ${this.advisor.lastname}`,
            },
            {
                email: this.customer.email,
                name: `${this.customer.firstname} ${this.customer.lastname}`,
            },
            `${subject}`,
            message,
            base64documents.concat(files)
        );
    }

    protected filterDocuments(documents: Document[]) {
        const product = this.product;
        if (!product) {
            return documents;
        }
        return documents.filter((document: Document) => {
            if (document.condition) {
                // tslint:disable-next-line:no-eval
                return eval(document.condition);
            } else {
                return true;
            }
        });
    }

    protected replaceItemsLabel(items, search, replace) {
        return map(items, (item) => {
            return {
                name: item.name,
                label: item.label.replace(search, replace),
            };
        });
    }
}



// WEBPACK FOOTER //
// ./src/helper/Product.helper.ts